import { Fragment } from "react";
import { Check, X, Minus } from "lucide-react";

// Capabilities grouped under AgriHub's three signature pillars — Discover, Collaborate, Transact —
// compared against typical categories of tools in use today.
const PILLARS = [
  {
    name: "Discover",
    rows: [
      { label: "Farm-to-Market Traceability", erp: "partial", mkt: "no", agri: "yes" },
      { label: "Market & Buyer Discovery", erp: "no", mkt: "yes", agri: "yes" },
      { label: "Portfolio Oversight (Dashboard)", erp: "no", mkt: "no", agri: "yes" },
      { label: "Inventory / Asset Tracking", erp: "yes", mkt: "no", agri: "yes" },
      { label: "Credit Eligibility & DPR-Based Assessment", erp: "no", mkt: "no", agri: "yes" },
    ],
    subtotal: ["1.5 / 5", "1 / 5", "5 / 5"],
  },
  {
    name: "Collaborate",
    rows: [
      { label: "External Ecosystem Connectivity", erp: "no", mkt: "yes", agri: "yes" },
      { label: "Multi-Stakeholder Coordination", erp: "no", mkt: "partial", agri: "yes" },
      { label: "Local Ecosystem / Virtual Cooperative Formation", erp: "no", mkt: "no", agri: "yes" },
      { label: "FPOs & Pooled Procurement", erp: "no", mkt: "partial", agri: "yes" },
      { label: "Configurable Across Verticals", erp: "no", mkt: "no", agri: "yes" },
    ],
    subtotal: ["0 / 5", "2 / 5", "5 / 5"],
  },
  {
    name: "Transact",
    rows: [
      { label: "Trade Creation & Digital Record", erp: "yes", mkt: "yes", agri: "yes" },
      { label: "Event-Based & Value-Chain Finance", erp: "no", mkt: "no", agri: "yes" },
      { label: "Settlement & Reconciliation", erp: "yes", mkt: "partial", agri: "yes" },
      { label: "Logistics & Dispatch Execution", erp: "no", mkt: "yes", agri: "yes" },
      { label: "Value-Added Service Billing", erp: "partial", mkt: "no", agri: "yes" },
    ],
    subtotal: ["2.5 / 5", "2.5 / 5", "5 / 5"],
  },
];

const TOTAL = ["4 / 15", "5 / 15", "15 / 15"];

const Mark = ({ value }) => {
  if (value === "yes") return <Check className="mark-yes" size={17} strokeWidth={2.75} aria-label="Full support" />;
  if (value === "partial") return <Minus className="mark-partial" size={17} strokeWidth={2.75} aria-label="Partial support" />;
  return <X className="mark-no" size={17} strokeWidth={2.75} aria-label="Not supported" />;
};

export default function PlatformComparisonTable() {
  return (
    <div data-testid="comparison-panel" className="comparison-panel reveal">
      <div className="comparison-intro">
        <span className="section-label">Where AgriHub Stands Apart</span>
        <h3>How the pillars compare</h3>
        <p>
          Capabilities grouped into AgriHub&apos;s three pillars - Discover, Collaborate and Transact
          compared against typical categories of tools in use today.
        </p>
      </div>

      <div className="comparison-table-scroll">
        <table className="comparison-table" data-testid="comparison-table">
          <thead>
            <tr>
              <th>Capability</th>
              <th>Enterprise Mgmt. Software (ERP-type)</th>
              <th>Agri Marketplace &amp; Logistics Platforms</th>
              <th className="agrihub-col">Valchain AgriHub</th>
            </tr>
          </thead>
          <tbody>
            {PILLARS.map((pillar) => (
              <Fragment key={pillar.name}>
                <tr className="pillar-row">
                  <td colSpan={4}>{pillar.name}</td>
                </tr>
                {pillar.rows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td><Mark value={row.erp} /></td>
                    <td><Mark value={row.mkt} /></td>
                    <td className="agrihub-col"><Mark value={row.agri} /></td>
                  </tr>
                ))}
                <tr className="subtotal-row">
                  <td>Pillar Subtotal</td>
                  <td>{pillar.subtotal[0]}</td>
                  <td>{pillar.subtotal[1]}</td>
                  <td className="agrihub-col">{pillar.subtotal[2]}</td>
                </tr>
              </Fragment>
            ))}
            <tr className="total-row">
              <td>Total Capability Coverage</td>
              <td>{TOTAL[0]}</td>
              <td>{TOTAL[1]}</td>
              <td className="agrihub-col">{TOTAL[2]}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="comparison-footnote">
        <Check className="mark-yes" size={20}/> = full support &nbsp;·&nbsp;
        <Minus className="mark-partial" size={20}/> = partial support &nbsp;·&nbsp;
        <X className="mark-no" size={20}/> = not supported.
        Scores reflect platform capability; results in any specific deployment will vary.
      </p>
    </div>
  );
}
